import { getIdToken } from './firebase';

/** Códigos de error tipados que devuelve qr-api ({error, code, correlationId}). */
export type ApiErrorKind =
  | 'unauthorized'
  | 'mfa_required'
  | 'forbidden'
  | 'not_found'
  | 'conflict'
  | 'validation'
  | 'rate_limited'
  | 'server'
  | 'network';

export class ApiError extends Error {
  readonly kind: ApiErrorKind;
  readonly status: number;
  readonly code?: string;
  readonly correlationId?: string;
  readonly retryAfterSeconds?: number;
  readonly details?: unknown;

  constructor(init: {
    kind: ApiErrorKind;
    status: number;
    message: string;
    code?: string;
    correlationId?: string;
    retryAfterSeconds?: number;
    details?: unknown;
  }) {
    super(init.message);
    this.name = 'ApiError';
    this.kind = init.kind;
    this.status = init.status;
    this.code = init.code;
    this.correlationId = init.correlationId;
    this.retryAfterSeconds = init.retryAfterSeconds;
    this.details = init.details;
  }
}

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  /** Clave de idempotencia para activaciones y creaciones (R5.3). */
  idempotencyKey?: string;
  /** Organización activa; el backend igual la valida contra el token (R1.3). */
  orgId?: string;
  signal?: AbortSignal;
}

export interface ApiClientConfig {
  baseUrl?: string;
  tokenProvider?: () => Promise<string | null>;
  fetchImpl?: typeof fetch;
  newCorrelationId?: () => string;
}

function defaultCorrelationId(): string {
  return globalThis.crypto?.randomUUID?.() ?? `cid-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function kindForStatus(status: number, code?: string): ApiErrorKind {
  if (status === 401) return 'unauthorized';
  if (status === 403) return code === 'mfa_required' ? 'mfa_required' : 'forbidden';
  if (status === 404) return 'not_found';
  if (status === 409) return 'conflict';
  if (status === 422 || status === 400) return 'validation';
  if (status === 429) return 'rate_limited';
  return 'server';
}

export function createApiClient(config: ApiClientConfig = {}) {
  const baseUrl = (config.baseUrl ?? import.meta.env.VITE_API_BASE_URL ?? '').replace(/\/$/, '');
  const tokenProvider = config.tokenProvider ?? (() => getIdToken());
  const doFetch = config.fetchImpl ?? ((...args: Parameters<typeof fetch>) => fetch(...args));
  const newCorrelationId = config.newCorrelationId ?? defaultCorrelationId;

  async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
    const correlationId = newCorrelationId();
    const headers: Record<string, string> = {
      Accept: 'application/json',
      'X-Correlation-Id': correlationId,
    };
    const token = await tokenProvider();
    if (token) headers.Authorization = `Bearer ${token}`;
    if (options.idempotencyKey) headers['Idempotency-Key'] = options.idempotencyKey;
    if (options.orgId) headers['X-Org-Id'] = options.orgId;
    if (options.body !== undefined) headers['Content-Type'] = 'application/json';

    let response: Response;
    try {
      response = await doFetch(`${baseUrl}${path}`, {
        method: options.method ?? 'GET',
        headers,
        body: options.body !== undefined ? JSON.stringify(options.body) : undefined,
        signal: options.signal,
      });
    } catch (err) {
      throw new ApiError({ kind: 'network', status: 0, message: 'No pudimos conectarnos. Revisa tu conexión e intenta de nuevo.', correlationId, details: err });
    }

    const text = await response.text();
    let payload: unknown = undefined;
    if (text) {
      try {
        payload = JSON.parse(text);
      } catch {
        payload = text;
      }
    }

    if (!response.ok) {
      const obj = (payload && typeof payload === 'object' ? payload : {}) as Record<string, unknown>;
      const code = typeof obj.code === 'string' ? obj.code : undefined;
      const retryHeader = response.headers.get('Retry-After');
      throw new ApiError({
        kind: kindForStatus(response.status, code),
        status: response.status,
        message: typeof obj.error === 'string' ? obj.error : `Error ${response.status}`,
        code,
        correlationId: typeof obj.correlationId === 'string' ? obj.correlationId : correlationId,
        retryAfterSeconds: retryHeader ? Number(retryHeader) : undefined,
        details: obj.details,
      });
    }
    return payload as T;
  }

  return {
    request,
    get: <T>(path: string, options?: Omit<RequestOptions, 'method' | 'body'>) => request<T>(path, { ...options, method: 'GET' }),
    post: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'method' | 'body'>) => request<T>(path, { ...options, method: 'POST', body }),
    patch: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'method' | 'body'>) => request<T>(path, { ...options, method: 'PATCH', body }),
    del: <T>(path: string, options?: Omit<RequestOptions, 'method' | 'body'>) => request<T>(path, { ...options, method: 'DELETE' }),
  };
}

export type ApiClient = ReturnType<typeof createApiClient>;

/** Cliente por defecto de la SPA. */
export const api = createApiClient();

export function newIdempotencyKey(): string {
  return defaultCorrelationId();
}
